/**
 * Halloween clipart — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenClipartPack: ToilePack = {
    slug: 'halloween-clipart',
    category: 'season',
    name: 'Halloween clipart',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'pumpkin', label: 'Carved pumpkin', subject: 'A carved pumpkin with a jagged grin and a stem, isolated on a plain white ground'},
        {key: 'cauldron', label: 'Cauldron', subject: 'A black cauldron bubbling with green potion, isolated on a plain white ground'},
        {key: 'spider', label: 'Spider', subject: 'A small black spider hanging from a thread, isolated on a plain white ground'},
    ],
    styles: [
        {key: 'cute', label: 'Spooky cute', style: 'kawaii spooky illustration, rounded shapes, soft outlines, friendly faces', palette: 'Pastel purple, orange, mint and cream with black accents', framing: 'Centered on a solid background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: '1950s halloween paper decoration style, halftone grain, slight misregistration', palette: 'Orange, black, cream and a faded green', framing: 'Centered on an aged cream paper background with an even margin'},
        {key: 'bold', label: 'Bold graphic', style: 'bold graphic illustration, thick outlines, high contrast, flat fills', palette: 'Orange, black and white with a single purple', framing: 'Centered on a solid background with a little room around it'},
    ],
    directions: ['a black cat instead', 'as a rainbow set', "for a kids' craft project"],
    directionHint: 'try: a black cat instead',
    seo: {
        title: 'Halloween Clipart: Pumpkin, Cauldron, Spider Art',
        metaDescription: 'Nine halloween clipart designs -- carved pumpkin, cauldron, spider -- in spooky cute, vintage, and bold graphic finishes. Isolated on white.',
        h1: 'Halloween clipart: carved pumpkin, cauldron, and spider',
        tagline: 'Nine graphics, three October objects, three finishes each.',
        intro: "Nine halloween clipart designs isolated on plain white: a carved pumpkin, a bubbling cauldron, and a small spider on a thread. Three finishes -- spooky cute, vintage, and bold graphic -- for flyers, worksheets, or a kids' craft project. Ask for a black cat instead, or a rainbow set, and the same three finishes carry it.",
        useCases: [
            'an October flyer or newsletter',
            'a classroom worksheet accent',
            "a kids' craft project",
            'a printable party invitation',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine clipart designs -- a carved pumpkin, a cauldron, and a spider -- each in spooky cute, vintage, and bold graphic finishes, isolated on a plain white ground.'},
            {q: 'Is the background actually transparent?', a: 'The images render on a plain white ground; removing the background for a transparent PNG is a quick step in most image editors.'},
            {q: 'Can I use this in a printed classroom handout?', a: 'Yes -- the clipart prints clean at standard document resolution for flyers, worksheets, and handouts.'},
            {q: 'Can I get a different object, like a black cat?', a: "Yes -- the direction field takes any variation, including a black cat instead, a rainbow set, or a kids'-craft-project crop."},
        ],
    },
}
